import { useMutation, useQueryClient } from "@tanstack/react-query";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  ledgerChartCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { logger } from "@/core/utils/func/logger";
import { ledgerEndpoints } from "@/features/ledger/api/endpoints";
import type { ChartOfAccountDto } from "@/features/ledger/api/dto";

/**
 * Crée un compte comptable (`POST /ChartOfAccounts/add`) — réservé à
 * flashpay-superadmin / flashpay-admin. Le revenu de frais client et la
 * charge de commission agent ne doivent jamais partager le même compte
 * (guide §6.4).
 */
export const useCreateChartOfAccount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["ledger", "chart-of-accounts", "create"],
    mutationFn: (body: ChartOfAccountDto) =>
      mutateData({
        url: ledgerEndpoints.chartOfAccounts.add,
        reqBody: body as unknown as Record<string, unknown>,
        method: "POST",
      }) as Promise<ChartOfAccountDto>,
    onSuccess: () => {
      return invalidateAfterMutation(queryClient, ledgerChartCacheKeys(), {
        resetListPage: true,
      });
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useCreateChartOfAccount failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "ledger" }
      );
    },
  });
};
